import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { Modal, Button } from 'antd';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { OpSelect } from 'components/customAntd/DLS/OpSelect/OpSelect';
import Loader from 'components/customAntd/Loader';
import { fetchLocations } from 'store/slices/locationsSlice';
import { Location } from 'types/locationTypes';
import { useTranslation } from 'react-i18next';

interface SelectLocationModalProps {
    open: boolean;
    onClose: () => void;
    handleLocationSelect: (locationId: number) => void;
}

const SelectLocationModal: React.FC<SelectLocationModalProps> = ({ open, onClose, handleLocationSelect }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const selectedUserSites = useSelector((state: RootState) => state.users.selectedUserSites);
    const { locations } = useSelector((state: RootState) => state.locations);

    const [loading, setLoading] = useState<boolean>(false);
    const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const { t } = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                if (orgId && locations.length === 0) {
                    dispatch(fetchLocations({ orgId, status: 1 }));
                }

                if (globalLocationId === -1) {
                    if (selectedUserSites && selectedUserSites.length > 0) {
                        // Default to the user's default location
                        setSelectedLocation(selectedUserSites[0].id);
                    } else if (locations.length > 0) {
                        // Default to the first available location
                        setSelectedLocation(locations[0].id);
                    }
                } else {
                    setSelectedLocation(globalLocationId || null); // Use globalLocationId if it's valid
                }
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [dispatch, orgId, selectedUserSites, locations, globalLocationId]);

    const handleOk = async () => {
        if (selectedLocation !== null) {
            setIsProcessing(true);
            try {
                await handleLocationSelect(selectedLocation); // Wait for location select to complete
            } catch (error) {
                console.error('Failed to process location selection:', error);
                setIsProcessing(false);
            }
            // Modal will be closed by parent after handleLocationSelect completes
        }
    };

    return (
        <Modal
            title={t('selectLocationModal.title')}
            open={open}
            onOk={handleOk}
            onCancel={onClose}
            centered
            confirmLoading={isProcessing}
            footer={[
                <Button key="cancel" onClick={onClose} disabled={isProcessing}>
                    {t('selectLocationModal.cancel')}
                </Button>,
                <Button key="ok" type="primary" onClick={handleOk} loading={isProcessing}>
                    {t('selectLocationModal.ok')}
                </Button>,
            ]}
        >
            <OpSpace direction="vertical" size="large" style={{ display: 'flex', paddingTop: '16px' }}>
                {loading ? (
                    <Loader />
                ) : (
                    <OpSelect
                        value={selectedLocation !== null ? String(selectedLocation) : undefined}
                        options={locations.map((location: Location) => ({
                            label: location.name.toString(),
                            value: String(location.id),
                        }))}
                        onChange={(value) => setSelectedLocation(Number(value))}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                        }
                        loading={loading}
                        disabled={isProcessing}
                        style={{ width: '100%' }}
                    />

                )}
            </OpSpace>
        </Modal>
    );
};

export default SelectLocationModal;
